import { Accordion, AccordionTab } from "primereact/accordion";
import { Column } from "primereact/column";
import { DataTable } from "primereact/datatable";
import { useEffect, useRef, useState } from "react";
import { format } from "date-fns";
import { EmptyRecRepFilter, Filter, IRevenueRepFilter } from "../Filter";
import { Fetch, ILazyParams } from "../../../../shared/Fetch";
import { Toolbar } from "primereact/toolbar";
import { Button } from "primereact/button";
import { ProjectRevenueDetails } from "./ProjectRevenueDetails";
import { Rating } from "./Rating";
import { ILogin } from "../../../../models/Login";

interface IProps {
    toast: any
}

interface IRevenueList {
    login_ID?: number,
    branchName?: string,
    company_Name?: string,
    user_Employee_ID?: string,
    user_Name?: string,
    department?: string,
    team_Lead?: string,
    resource_Manager?: string,
    eci?: number,
    po?: number,
    incentive?: number,
    expenses?: number,
    seating_Cost?: number,
    revenue?: number,
    projected_Revenue?: number,
    monthly_CTC?: number,
    project_Rev_PL_Amnt?: number,
    approxProjRevM_Salary?: number,
    approxProjSeating_Cost?: number,
    maxDueDate?: Date,
    total: number,
}

export const Table = (props: IProps) => {
    const [totalRecords, setTotalRecords] = useState(0);
    const [tableData, setTableData] = useState({ tableData: [], loading: false } as { tableData: Array<IRevenueList>, loading: boolean });
    const [revenueFilter, setRevenueFilter] = useState<IRevenueRepFilter>(EmptyRecRepFilter);
    const [openProjectRevenueDetails, setOpenProjectRevenueDetails] = useState({ open: false, user_Employee_ID: '' } as { open: boolean, user_Employee_ID: string });

    const dt = useRef<any>(null);

    const _roles: Array<number> = [];
    const loginObj: ILogin = JSON.parse(sessionStorage.getItem("auth") + "");
    loginObj.roles.forEach((role) => {
        _roles.push(role.role_ID);
    });

    const [lazyParams, setLazyParams] = useState<ILazyParams>({
        first: 0,
        rows: 25,
        page: 1,
        sortField: 'login_ID',
        sortOrder: -1
    });

    const onPage = (event: any) => {
        delete event.filters;
        delete event.multiSortMeta;
        setLazyParams(event);
    }

    const onSort = (event: any) => {
        delete event.filters;
        delete event.multiSortMeta;
        setLazyParams(event);
    }

    const header = () => <p>Revenue Report</p>;

    const loadTableData = async () => {
        setTableData({ tableData: tableData.tableData, loading: true });
        const response = await Fetch("Report_Revenue", "GET", null, `lazyParams=${JSON.stringify(lazyParams)}&filter=${JSON.stringify(revenueFilter)}`, props.toast);
        if (response) {
            const data: Array<IRevenueList> = await response.json();
            if (data.length > 0) setTotalRecords(data[0].total || 0);
            else setTotalRecords(0);
            setTableData({ tableData: data, loading: false });
        } else setTableData({ tableData: tableData.tableData, loading: false });
    }

    const projectRevenue = (rowData: IRevenueList) => {
        return <p onClick={() => {
            if (rowData.projected_Revenue && rowData.projected_Revenue > 0) {
                setOpenProjectRevenueDetails({ open: true, user_Employee_ID: (rowData.user_Employee_ID || '') });
            } else props.toast.current.show({ severity: 'info', detail: 'No Projected Revenue Details To Show', life: 3000 });
        }} className="cursor-pointer text-cyan-700 underline">₹ {parseInt(`${rowData.projected_Revenue}`)}</p>;
    }

    const projectRevenuePL = (rowData: IRevenueList) => {
        return <p style={{ color: ((rowData.projected_Revenue || 0) < ((rowData.project_Rev_PL_Amnt || 0) + (rowData.approxProjRevM_Salary || 0) + (rowData.approxProjSeating_Cost || 0))) ? "red" : "green" }}>₹ {parseInt(`${(rowData.projected_Revenue || 0) - ((rowData.project_Rev_PL_Amnt || 0) + (rowData.approxProjRevM_Salary || 0) + (rowData.approxProjSeating_Cost || 0))}`)}</p>;
    }

    const exportCSV = () => {
        dt.current.exportCSV();
    };

    const leftToolbarTemplate = () => {
        return (
            <>
                <div className="my-2">
                    <Button icon="pi pi-refresh" className="p-button-rounded p-button-info mr-2" onClick={() => loadTableData()} />
                </div>
            </>
        );
    };

    const rightToolbarTemplate = () => {
        return (
            <>
                {(_roles.includes(1) || _roles.includes(12)) && <Button label="Export" icon="pi pi-upload" className="p-button-help" onClick={exportCSV} />}
            </>
        );
    }

    useEffect(() => {
        loadTableData();
    }, [lazyParams, revenueFilter]);

    // ₹

    return (
        <>
            <Toolbar left={leftToolbarTemplate} right={rightToolbarTemplate} className="mb-3" />
            <Accordion>
                <AccordionTab header="Filter">
                    <Filter revenueFilter={revenueFilter} setRevenueFilter={setRevenueFilter} showNormalUsers={true} showRecHeadUsers={false} showSalesHeadUsers={false} />
                </AccordionTab>
            </Accordion>
            <DataTable style={{ border: '1px solid rgba(105,105,105,0.17)', borderTopWidth: "0", marginTop: "5px" }}
                resizableColumns showGridlines columnResizeMode="expand" exportFilename={format(new Date(), 'dd-MM-yyyy hh_mm_ss a').toUpperCase()}
                ref={dt} size="small" stripedRows scrollable scrollHeight="70vh"
                lazy dataKey="login_ID" responsiveLayout="scroll"
                paginator first={lazyParams.first} rows={lazyParams.rows} rowsPerPageOptions={[25, 50, 100, 200]}
                totalRecords={totalRecords} onPage={onPage}
                onSort={onSort} sortField={lazyParams.sortField} sortOrder={lazyParams.sortOrder}
                emptyMessage="No Users found."
                paginatorTemplate="FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink CurrentPageReport RowsPerPageDropdown"
                currentPageReportTemplate="Showing {first} to {last} of {totalRecords}"
                value={tableData.tableData}
                header={header}
                loading={tableData.loading}
            >
                <Column field="sn" header="SN" body={(rowData, options) => options.rowIndex + 1} />
                <Column field="branchName" header="Branch" />
                <Column field="company_Name" header="Company" />
                <Column field="user_Employee_ID" header="EMP Code" />
                <Column field="user_Name" header="EMP Name" />
                <Column field="department" header="Department" />
                <Column field="team_Lead" header="TL" />
                <Column field="resource_Manager" header="RM" />
                <Column field="eci" header="ECI" />
                <Column field="po" header="PO" />
                <Column field="monthly_CTC" header="Monthly CTC" body={(rowData: IRevenueList) => <p>₹ {parseInt(`${rowData.monthly_CTC}`)}</p>} />
                <Column field="seating_Cost" header="Seating Cost" body={(rowData: IRevenueList) => <p>₹ {parseInt(`${rowData.seating_Cost}`)}</p>} />
                <Column field="incentive" header="Incentive" body={(rowData: IRevenueList) => <p>₹ {parseInt(`${rowData.incentive}`)}</p>} />
                <Column field="expenses" header="Expenses" body={(rowData: IRevenueList) => <p>₹ {parseInt(`${rowData.expenses}`)}</p>} />
                <Column field="revenue" header="Revenue" body={(rowData: IRevenueList) => <p>₹ {parseInt(`${rowData.revenue}`)}</p>} />
                <Column field="" header="R P/L" body={(rowData: IRevenueList) => <p style={{ color: ((rowData.expenses || 0) < (rowData.revenue || 0)) ? "green" : "red" }}>₹ {(((rowData.revenue || 0) < (rowData.expenses || 0))) ? parseInt(`${(-1 * ((rowData.revenue || 0) - (rowData.expenses || 0)))}`) : parseInt(`${((rowData.revenue || 0) - (rowData.expenses || 0))}`)}</p>} />
                <Column field="approxProjRevM_Salary" header="P.Salary" body={(rowData: IRevenueList) => <p>₹ {parseInt(`${rowData.approxProjRevM_Salary}`)}</p>} />
                <Column field="approxProjSeating_Cost" header="P.Seating Cost" body={(rowData: IRevenueList) => <p>₹ {parseInt(`${rowData.approxProjSeating_Cost}`)}</p>} />
                <Column field="project_Rev_PL_Amnt" header="P.Incentive" body={(rowData: IRevenueList) => <p>₹ {parseInt(`${rowData.project_Rev_PL_Amnt}`)}</p>} />
                <Column field="projected_Revenue" header="P.Revenue" body={projectRevenue} />
                <Column field="" header="P.R P/L" body={projectRevenuePL} />
                {/* <Column field="profit_Loss" header="Loss/Profit" />
                <Column field="projected_Revenue" header="P.Revenue" />
                <Column field="projected_Profit_Loss" header="P.Profit/Loss" /> */}
                <Column field="" header="Action" />
                <Column field="rating" header="Rating" headerStyle={{ minWidth: "12rem" }} bodyStyle={{ minWidth: "12rem" }} body={(rowData: IRevenueList) => <Rating rev={(rowData.revenue || 0)} exp={(rowData.expenses || 0)} />} />
            </DataTable>
            <ProjectRevenueDetails open={openProjectRevenueDetails.open} hideDialog={() => setOpenProjectRevenueDetails({ open: false, user_Employee_ID: '' })} user_Employee_ID={openProjectRevenueDetails.user_Employee_ID} revenueFilter={revenueFilter} toast={props.toast} />
        </>
    );
}